import { request, unauthenticatedRequest } from '../utils/request';
import type {
  CreateUserRequest,
  User,
  ActivationStatusResponse,
  LoginResponse,
  UpdateUserRequest,
  ActivateUserResponse,
  Manager,
  DownloadUsersRequestParams,
  ForgotPasswordResponse,
  ValidateResetTokenResponse,
  ResetPasswordResponse,
  BulkProvisionResponse,
} from '../types/user.types';

// Create user (authenticated)
export const createUser = async (
  userData: CreateUserRequest,
): Promise<{ message: string; user: User }> => {
  return request('/users', {
    method: 'POST',
    body: userData,
  });
};

// Check activation status (public)
export const checkActivationStatus = async (
  uuid: string,
): Promise<ActivationStatusResponse> => {
  return unauthenticatedRequest<ActivationStatusResponse>(
    `/users/activate/${uuid}`,
  );
};

// Activate user (public)
export const activateUser = async (
  uuid: string,
  password: string,
): Promise<ActivateUserResponse> => {
  return unauthenticatedRequest<ActivateUserResponse>(
    `/users/activate/${uuid}`,
    {
      method: 'POST',
      body: { password },
    },
  );
};

// Login (authenticated - requires Firebase token)
export const login = async (): Promise<LoginResponse> => {
  return request<LoginResponse>('/users/login', {
    method: 'POST',
  });
};

// Update user
export const updateUser = async (
  uuid: string,
  updateData: UpdateUserRequest,
): Promise<{ message: string; user: User }> => {
  return request(`/users/${uuid}`, {
    method: 'PUT',
    body: updateData,
  });
};

// Deactivate user
export const deactivateUser = async (
  uuid: string,
  options?: { newManagerUuid?: string },
): Promise<{
  message: string;
  hierarchyNode: { uuid: string; name: string } | null;
}> => {
  return request(`/users/${uuid}/deactivate`, {
    method: 'POST',
    body: options || {},
  });
};

// Reactivate user
export const reactivateUser = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request(`/users/${uuid}/reactivateUser`, {
    method: 'POST',
  });
};

// Delete user
export const deleteUser = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request(`/users/${uuid}`, {
    method: 'DELETE',
  });
};

// Read one user
export const readUser = async (uuid: string): Promise<{ user: User }> => {
  return request(`/users/${uuid}`);
};

// Read all users (with pagination)
export const readAllUsers = async (
  page = 1,
  pageSize = 10,
): Promise<{
  data: User[];
  page: number;
  pageSize: number;
  total: number;
}> => {
  return request(`/user?page=${page}&pageSize=${pageSize}`);
};

// Get all managers
export const getManagers = async (): Promise<{ managers: Manager[] }> => {
  return request('/users/managers');
};

// Download users as CSV
export const downloadUsersAsCsv = async (
  params: DownloadUsersRequestParams,
): Promise<void> => {
  // Build query string from params
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const url = `/users/download?${queryParams.toString()}`;

  // Use the request utility to get the blob
  const blob = await request<Blob>(url, {
    method: 'GET',
    responseType: 'blob',
  });

  // Create a download link and trigger it
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

// Forgot password (public)
export const forgotPassword = async (
  email: string,
): Promise<ForgotPasswordResponse> => {
  return unauthenticatedRequest<ForgotPasswordResponse>(
    '/users/forgot-password',
    {
      method: 'POST',
      body: { email },
    },
  );
};

// Validate reset token (public)
export const validateResetToken = async (
  token: string,
): Promise<ValidateResetTokenResponse> => {
  return unauthenticatedRequest<ValidateResetTokenResponse>(
    `/users/reset-password/${token}`,
  );
};

// Reset password (public)
export const resetPassword = async (
  token: string,
  password: string,
): Promise<ResetPasswordResponse> => {
  return unauthenticatedRequest<ResetPasswordResponse>(
    `/users/reset-password/${token}`,
    {
      method: 'POST',
      body: { password },
    },
  );
};

// Bulk provision users
export const bulkProvisionUsers = async (
  file: File,
  accountUuid: string,
  sendActivation: boolean,
): Promise<BulkProvisionResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountUuid', accountUuid);
  formData.append('sendActivation', String(sendActivation));

  return request('/users/bulk-provision', {
    method: 'POST',
    body: formData,
    isFormData: true,
  });
};

/** Public by necessity — the driver has no session yet, so there is no token to send. */
export const driverLogin = async (
  driverId: string,
  password: string,
): Promise<{ token: string; mustSetPassword: boolean }> => {
  return unauthenticatedRequest('/users/driver-login', {
    method: 'POST',
    body: { driverId, password },
  });
};

/** Authenticated: the driver is signed in by this point, they just owe us a password. */
export const setPassword = async (
  password: string,
): Promise<{ message: string }> => {
  return request('/users/set-password', {
    method: 'POST',
    body: { password },
  });
};

/** Issues a new temporary password. The plaintext comes back once and is never stored
 *  in readable form, so the caller must show it immediately. */
export const resetDriverPassword = async (
  uuid: string,
): Promise<{ temporaryPassword: string }> => {
  return request(`/users/${uuid}/reset-password`, { method: 'POST' });
};
